import * as ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { ApplicationBoundary, type ClerkProviderConfig } from './ApplicationBoundary';
import { StaleBuildBanner } from './components/StaleBuildBanner';
import { BRAND_CONFIGS, isOnHlxHost, swapApex } from './config/branding';
import { initializeBranding } from './config/branding-utils';
import { primeImpersonationDelegation } from './features/user/impersonationHandoff';
import './fonts';
import { initObservability } from './observability/init';
import { authDebug, summarizeUrl } from './routing/authDebug';
import {
  clerkCdnVersionPins,
  loadBundledClerkOptions,
  shouldBundleClerk,
} from './routing/clerkBundle';
import { buildClerkSatelliteOptions } from './routing/clerkSatellite';
import { paths } from './routing/routes';
import './styles.css';

// RUM tracing must start before any other bootstrap so the branding fetch is captured.
initObservability();

// Single-container model: brand detected at runtime from URL path (/vault → mcv, /hlx → hlx).
// BrandSync re-runs initializeBranding() on every route change; BrandProvider provides brand context to layout subtrees.
initializeBranding();

// Closes the impersonation-handoff race: dashboard data-fetches mount before
// the XState machine reaches `$_AUTHENTICATED_IMPERSONATING`, so the axios
// `X-Acting-As-Provider` header must be primed before any subtree renders.
primeImpersonationDelegation();

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

// Satellite mode runs whenever we're on the HLX host (apex or any subdomain).
// Each env has its own Clerk instance with its own satellite registration:
//   prod:    apex `healthcarelx.com`     (pk_live, FAPI at clerk.healthcarelx.com)
//   staging: `staging.healthcarelx.com`  (pk_test, FAPI on *.clerk.accounts.dev)
//   dev:     `dev.healthcarelx.com`      (pk_test, FAPI on *.clerk.accounts.dev)
// There are no clerk.staging.* or clerk.dev.* DNS records. The satellite options
// (domain, autosync) are built by buildClerkSatelliteOptions.
const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY || '__CLERK_KEY_PLACEHOLDER__';
const hostname = window.location.hostname;
const MCV_APEX = BRAND_CONFIGS.mcv.domain;
const HLX_APEX = BRAND_CONFIGS.hlx.domain;
const useSatellite = isOnHlxHost(hostname);
const satelliteOptions = buildClerkSatelliteOptions({
  hostname,
  pathname: window.location.pathname,
  publishableKey,
});

// Primary origin is the matching MCV host with the same env prefix; when the
// HLX host is the apex (prod), the swap yields the bare MCV apex which doesn't
// serve auth, so fall back to prod.medcredvault.com.
const primaryHost = useSatellite ? swapApex(hostname, HLX_APEX, MCV_APEX) : '';
const primaryOrigin = useSatellite
  ? primaryHost === MCV_APEX
    ? 'https://prod.medcredvault.com'
    : `https://${primaryHost}`
  : import.meta.env.VITE_VAULT_DOMAIN;
const signInUrl =
  useSatellite && primaryOrigin
    ? `${primaryOrigin}${paths.publicAuth.signIn}`
    : paths.publicAuth.signIn;
const signUpUrl =
  useSatellite && primaryOrigin
    ? `${primaryOrigin}${paths.publicAuth.signUp}`
    : paths.publicAuth.signUp;

// [MCV-1789] On pk_test (dev and staging) clerk-js and Clerk UI are bundled and
// loaded first-party instead of from *.clerk.accounts.dev; see clerkBundle.ts.
// The download starts here at boot, in parallel with render. ApplicationBoundary
// holds back only the trees that mount Clerk until it settles (a failed or stalled
// import resolves to {}, the CDN fallback); Clerk-free public pages never wait.
// Production passes nothing and keeps its CDN loading and its first-render mount.
const bundledClerk = shouldBundleClerk(publishableKey)
  ? loadBundledClerkOptions(publishableKey)
  : undefined;

const clerkConfig: ClerkProviderConfig = {
  publishableKey,
  standardBrowser: import.meta.env.NODE_ENV !== 'test',
  signInUrl,
  signUpUrl,
  signInFallbackRedirectUrl: paths.publicAuth.authComplete,
  signUpFallbackRedirectUrl: paths.publicAuth.authComplete,
  // Core 3 hides optional sign-up fields by default. Preserve the Core 2
  // registration UI while product decides whether to intentionally simplify
  // those forms in a separate change.
  appearance: {
    options: {
      showOptionalFields: true,
    },
  },
  taskUrls: {
    'choose-organization': paths.sessionTasks.chooseOrganization,
    'reset-password': paths.sessionTasks.resetPassword,
    'setup-mfa': paths.sessionTasks.setupMfa,
  },
  // List every env's apex+subdomain pair so handshake redirects validate in
  // dev/staging/prod regardless of where the bundle was built.
  allowedRedirectOrigins: [
    import.meta.env.VITE_VAULT_DOMAIN,
    import.meta.env.VITE_HLX_DOMAIN,
    // Bare apexes ensure prod's apex-registered Clerk satellite redirects
    // validate even if VITE_*_DOMAIN is ever overridden to a subdomain.
    `https://${BRAND_CONFIGS.mcv.domain}`,
    `https://${BRAND_CONFIGS.hlx.domain}`,
    'https://prod.medcredvault.com',
    'https://staging.medcredvault.com',
    'https://dev.medcredvault.com',
    'https://prod.healthcarelx.com',
    'https://staging.healthcarelx.com',
    'https://dev.healthcarelx.com',
  ].filter(Boolean),
  // Core 3 no longer synchronizes an existing primary-domain session on a
  // first satellite visit unless this is enabled. Keep the established MCV ->
  // HLX experience during the SDK migration, except on the public site pages
  // [MCV-1719]; see buildClerkSatelliteOptions for why and what still syncs.
  ...satelliteOptions,
  // [MCV-1790] Pin the CDN-loaded clerk-js / Clerk UI to the installed versions. A
  // bundled Clerk makes the SDK skip the CDN script, so these pins only matter on
  // pk_live and on the pk_test CDN fallback.
  ...clerkCdnVersionPins(),
};

authDebug('clerk.bootstrap', {
  hostname,
  useSatellite,
  satelliteAutoSync: satelliteOptions.satelliteAutoSync ?? false,
  satelliteDomain: satelliteOptions.domain ?? null,
  primaryOrigin,
  signInUrl: summarizeUrl(signInUrl),
  signUpUrl: summarizeUrl(signUpUrl),
  // Intent only: a failed or stalled bundle falls back to the CDN, and the outcome
  // is logged as `clerk.source` once loadBundledClerkOptions settles.
  clerkSource: bundledClerk ? 'bundle-requested' : 'cdn',
});

root.render(
  <BrowserRouter>
    <ApplicationBoundary clerkConfig={clerkConfig} bundledClerk={bundledClerk} />
    {/* [MCV-1948] Outside the boundary so it shows on every page, signed in or not */}
    <StaleBuildBanner />
  </BrowserRouter>
);
